/**
 * Color Palette for WinFacil Application
 * 
 * This file defines the complete color system including:
 * - Primary brand colors (greens)
 * - Semantic colors (success, error, warning, info)
 * - Neutral grays for backgrounds and text
 * - Accessibility-compliant contrast ratios (WCAG AA)
 */

const palette = {
  neutral100: '#FFFFFF',
  neutral200: '#F4F2F1',
  neutral300: '#D7CEC9',
  neutral400: '#B6ACA6',
  neutral500: '#978F8A',
  neutral600: '#564E4A',
  neutral700: '#3C3836',
  neutral800: '#191015',
  neutral900: '#009A56',

  primary100: '#30C273',
  primary200: '#009A56',
  primary300: '#008457',
  primary400: '#006E54',
  primary500: '#00594C',
  primary600: '#004946',

  secondary100: '#C7F9CF',
  secondary200: '#92F4AC',
  secondary300: '#59E08A',
  secondary400: '#30C273',
  secondary500: '#009A56',

  accent100: '#FFEED4',
  accent200: '#FFE1B2',
  accent300: '#FDD495',
  accent400: '#FBC878',
  accent500: '#FFBB50',

  angry100: '#F2D6CD',
  angry500: '#C03403',

  overlay20: 'rgba(25, 16, 21, 0.2)',
  overlay50: 'rgba(25, 16, 21, 0.5)',

  'color-primary-100': '#C7F9CF',
  'color-primary-200': '#92F4AC',
  'color-primary-300': '#59E08A',
  'color-primary-400': '#30C273',
  'color-primary-500': '#009A56',
  'color-primary-600': '#008457',
  'color-primary-700': '#006E54',
  'color-primary-800': '#00594C',
  'color-primary-900': '#004946',
  'color-success-100': '#DCFBD6',
  'color-success-200': '#B3F7AE',
  'color-success-300': '#82E884',
  'color-success-400': '#5ED26C',
  'color-success-500': '#30B54D',
  'color-success-600': '#239B48',
  'color-success-700': '#188243',
  'color-success-800': '#0F683C',
  'color-success-900': '#095637',
  'color-info-100': '#D3E6FE',
  'color-info-200': '#A9CBFD',
  'color-info-300': '#7DACFA',
  'color-info-400': '#5C91F5',
  'color-info-500': '#2867EF',
  'color-info-600': '#1D4FCD',
  'color-info-700': '#143AAC',
  'color-info-800': '#0C288A',
  'color-info-900': '#071B72',
  'color-warning-100': '#FEFACB',
  'color-warning-200': '#FEF598',
  'color-warning-300': '#FDED65',
  'color-warning-400': '#FBE53E',
  'color-warning-500': '#F9D800',
  'color-warning-600': '#D6B600',
  'color-warning-700': '#B39600',
  'color-warning-800': '#907700',
  'color-warning-900': '#776100',
  'color-danger-100': '#FDDCD2',
  'color-danger-200': '#FCB2A7',
  'color-danger-300': '#F67E7A',
  'color-danger-400': '#ED575F',
  'color-danger-500': '#E22440',
  'color-danger-600': '#C21A42',
  'color-danger-700': '#A21242',
  'color-danger-800': '#830B3E',
  'color-danger-900': '#6C063B',
} as const;

export const colors = {
  /**
   * The palette is available to use, but prefer using the name.
   * This is only included for rare, one-off cases. Try to use
   * semantic names as much as possible.
   */
  palette,
  
  /**
   * A helper for making something see-thru.
   */
  transparent: 'rgba(0, 0, 0, 0)',
  
  /**
   * Primary brand color - Main green
   */
  primary: palette.primary500,
  primaryLight: palette.primary100,
  primaryDark: palette.primary600,
  
  /**
   * Secondary brand colors
   */
  secondary: palette.secondary400,
  secondaryLight: palette.secondary100,
  secondaryDark: palette.secondary500,
  
  /**
   * Text colors with accessibility-compliant contrast
   */
  text: palette.neutral700,
  textSecondary: palette.neutral600,
  textDisabled: palette.neutral400,
  textInverse: palette.neutral100,
  
  /**
   * Background colors
   */
  background: palette.neutral200,
  backgroundPaper: palette.neutral100,
  backgroundDark: palette.neutral700,
  
  /**
   * Border and divider colors
   */
  border: palette.neutral400,
  borderLight: palette.neutral300,
  divider: palette.neutral300,
  
  /**
   * Semantic colors for feedback
   */
  success: palette['color-success-500'],
  successLight: palette['color-success-100'],
  successDark: palette['color-success-700'],
  
  error: palette.angry500,
  errorLight: palette.angry100,
  errorDark: palette['color-danger-700'],
  
  warning: palette['color-warning-500'],
  warningLight: palette['color-warning-100'],
  warningDark: palette['color-warning-700'],
  
  info: palette['color-info-500'],
  infoLight: palette['color-info-100'],
  infoDark: palette['color-info-700'],
  
  /**
   * Accent color for highlights
   */
  accent: palette.accent500,
  accentLight: palette.accent100,
  accentDark: palette.accent400,
  
  /**
   * Overlay colors for modals and dialogs
   */
  overlay20: palette.overlay20,
  overlay50: palette.overlay50,
  overlay75: 'rgba(25, 16, 21, 0.75)',
  
  /**
   * The main tinting color.
   */
  tint: palette.primary500,
  
  /**
   * A subtle color used for lines.
   */
  separator: palette.neutral300,
};

/**
 * Helper function to create rgba color with custom opacity
 */
export const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
